import type { Lang } from '@/i18n';

/**
 * Hero copy is split out of HeroSection.tsx to keep the component file small
 * and editable. All maps below take the question count `n` (already locale
 * formatted) where it appears in copy. Update copy here, not in the component.
 */

type CtaPair = { speaking: string; writing: string };
type FeaturePack = { pool: string; speaking: string; writing: string; score: string };
type CardCopy = { title: string; desc: string; cta: string };
type Headline = { l1: string; l2: string };

export const ctaLabels: Record<Lang, CtaPair> = {
  ko: { speaking: '스피킹 연습', writing: '라이팅 연습' },
  ja: { speaking: 'スピーキング', writing: 'ライティング' },
  vi: { speaking: 'Luyện nói', writing: 'Luyện viết' },
  th: { speaking: 'ฝึกพูด', writing: 'ฝึกเขียน' },
  id: { speaking: 'Latihan Bicara', writing: 'Latihan Menulis' },
  en: { speaking: 'Speaking Practice', writing: 'Writing Practice' },
  ms: { speaking: 'Latihan Bertutur', writing: 'Latihan Menulis' },
  my: { speaking: 'စကားပြော လေ့ကျင့်မှု', writing: 'စာရေး လေ့ကျင့်မှု' },
  de: { speaking: 'Sprechtraining', writing: 'Schreibtraining' },
  es: { speaking: 'Práctica de Speaking', writing: 'Práctica de Writing' },
  fr: { speaking: 'Pratique Speaking', writing: 'Pratique Writing' },
  ar: { speaking: 'تدريب التحدث', writing: 'تدريب الكتابة' },
  'pt-BR': { speaking: 'Prática de Fala', writing: 'Prática de Escrita' },
  'zh-TW': { speaking: '口說練習', writing: '寫作練習' },
  'zh-CN': { speaking: '口语练习', writing: '写作练习' },
};

export const featureLabels = (n: string): Record<Lang, FeaturePack> => ({
  ko: { pool: `${n}+ 문항`, speaking: 'Speaking 연습', writing: 'Writing 연습', score: '점수 변환표' },
  ja: { pool: `${n}+問題`, speaking: 'Speaking練習', writing: 'Writing練習', score: 'スコア換算表' },
  vi: { pool: `${n}+ câu`, speaking: 'Luyện Speaking', writing: 'Luyện Writing', score: 'Bảng quy đổi' },
  th: { pool: `${n}+ ข้อ`, speaking: 'ฝึก Speaking', writing: 'ฝึก Writing', score: 'ตารางคะแนน' },
  id: { pool: `${n}+ soal`, speaking: 'Latihan Speaking', writing: 'Latihan Writing', score: 'Tabel Skor' },
  en: { pool: `${n}+ items`, speaking: 'Speaking Practice', writing: 'Writing Practice', score: 'Score Chart' },
  ms: { pool: `${n}+ soalan`, speaking: 'Latihan Bertutur', writing: 'Latihan Menulis', score: 'Carta Skor' },
  my: { pool: `${n}+ မေးခွန်း`, speaking: 'စကားပြော လေ့ကျင့်မှု', writing: 'စာရေး လေ့ကျင့်မှု', score: 'ရမှတ်ဇယား' },
  de: { pool: `${n}+ Aufgaben`, speaking: 'Sprechtraining', writing: 'Schreibtraining', score: 'Punktetabelle' },
  es: { pool: `${n}+ preguntas`, speaking: 'Práctica Speaking', writing: 'Práctica Writing', score: 'Tabla de puntaje' },
  fr: { pool: `${n}+ questions`, speaking: 'Pratique Speaking', writing: 'Pratique Writing', score: 'Barème des scores' },
  ar: { pool: `${n}+ سؤال`, speaking: 'تدريب التحدث', writing: 'تدريب الكتابة', score: 'جدول الدرجات' },
  'pt-BR': { pool: `${n}+ questões`, speaking: 'Prática de Fala', writing: 'Prática de Escrita', score: 'Tabela de Pontos' },
  'zh-TW': { pool: `${n}+ 題`, speaking: '口說練習', writing: '寫作練習', score: '分數換算表' },
  'zh-CN': { pool: `${n}+ 题`, speaking: '口语练习', writing: '写作练习', score: '分数换算表' },
});

export const quickStartLabels: Record<Lang, CardCopy> = {
  ko: { title: '빠른 시작', desc: '토플 무료 모의고사 안내 → 모의고사 센터에서 섹션 응시. 가입 없이 가능.', cta: '무료 모의고사 안내' },
  ja: { title: 'クイックスタート', desc: '無料模試で今のスコアと弱点を15分で確認。', cta: '無料模試を開始' },
  vi: { title: 'Bắt đầu nhanh', desc: 'Làm mock test miễn phí và biết điểm hiện tại cùng điểm yếu trong 15 phút.', cta: 'Bắt đầu mock test' },
  th: { title: 'เริ่มแบบเร็ว', desc: 'ทำข้อสอบจำลองฟรีเพื่อรู้คะแนนและจุดอ่อนภายใน 15 นาที', cta: 'เริ่มข้อสอบฟรี' },
  id: { title: 'Mulai Cepat', desc: 'Cek skor saat ini dan titik lemah dalam 15 menit lewat mock test gratis.', cta: 'Mulai mock test gratis' },
  en: { title: 'Quick Start', desc: 'Take a free mock test to check your current score and weak spots in about 15 minutes.', cta: 'Start free mock test' },
  ms: { title: 'Mula Pantas', desc: 'Semak skor semasa dan kelemahan anda dalam 15 minit melalui mock test percuma.', cta: 'Mula mock test percuma' },
  my: { title: 'အမြန်စတင်', desc: 'အခမဲ့ mock test ဖြင့် ၁၅ မိနစ်အတွင်း လက်ရှိရမှတ်နှင့် အားနည်းချက်များကို စစ်ဆေးပါ။', cta: 'အခမဲ့ mock test စတင်' },
  de: { title: 'Schnellstart', desc: 'Machen Sie einen kostenlosen Mock-Test und prüfen Sie Score und Schwächen in 15 Minuten.', cta: 'Kostenlosen Mock-Test starten' },
  es: { title: 'Inicio rápido', desc: 'Haz un simulacro gratis y detecta tu puntuación y puntos débiles en 15 minutos.', cta: 'Empezar simulacro gratis' },
  fr: { title: 'Démarrage rapide', desc: 'Faites un mock test gratuit et identifiez votre score et vos points faibles en 15 minutes.', cta: 'Commencer le mock test gratuit' },
  ar: { title: 'بداية سريعة', desc: 'ابدأ اختبارًا مجانيًا لتقدير درجتك ونقاط ضعفك خلال 15 دقيقة.', cta: 'ابدأ الاختبار المجاني' },
  'pt-BR': { title: 'Início Rápido', desc: 'Faça um simulado grátis e descubra sua pontuação e pontos fracos em 15 minutos.', cta: 'Começar simulado grátis' },
  'zh-TW': { title: '快速開始', desc: '用免費模考在 15 分鐘內確認目前分數與弱點。', cta: '開始免費模考' },
  'zh-CN': { title: '快速开始', desc: '通过免费模考在 15 分钟内确认当前分数和弱点。', cta: '开始免费模考' },
};

export const scorePlanLabels: Record<Lang, CardCopy> = {
  ko: { title: '목표 점수 플랜', desc: '60/80/100+ 목표별 학습 루트와 약점 보완 방법을 바로 확인하세요.', cta: '학습 루트 보기' },
  ja: { title: '目標スコアプラン', desc: '60/80/100+ 目標別の学習ルートと弱点対策をすぐ確認。', cta: '学習ルートを見る' },
  vi: { title: 'Lộ trình điểm mục tiêu', desc: 'Xem lộ trình học và cách khắc phục điểm yếu cho mục tiêu 60/80/100+.', cta: 'Xem lộ trình học' },
  th: { title: 'แผนคะแนนเป้าหมาย', desc: 'ดูแผนเรียนและวิธีแก้จุดอ่อนตามเป้าหมาย 60/80/100+ ได้ทันที', cta: 'ดูแผนเรียน' },
  id: { title: 'Rencana skor target', desc: 'Lihat jalur belajar dan cara menutup kelemahan untuk target 60/80/100+.', cta: 'Lihat jalur belajar' },
  en: { title: 'Goal Score Plan', desc: 'See the study path and weakness fix for 60/80/100+ targets right away.', cta: 'See the study path' },
  ms: { title: 'Pelan skor sasaran', desc: 'Lihat laluan belajar dan cara baiki kelemahan untuk sasaran 60/80/100+.', cta: 'Lihat laluan belajar' },
  my: { title: 'ရမှတ်ရည်မှန်းချက် ပလန်', desc: '60/80/100+ အတွက် လေ့လာမှုလမ်းကြောင်းနှင့် အားနည်းချက်ပြင်ဆင်မှုကို ချက်ချင်းကြည့်ပါ။', cta: 'လေ့လာမှုလမ်းကြောင်းကြည့်ရန်' },
  de: { title: 'Zielscore-Plan', desc: 'Sehen Sie Lernpfad und Schwächenanalyse für 60/80/100+ sofort an.', cta: 'Lernpfad ansehen' },
  es: { title: 'Plan de puntuación objetivo', desc: 'Ver ruta de estudio y cómo corregir debilidades para 60/80/100+.', cta: 'Ver ruta de estudio' },
  fr: { title: 'Plan de score cible', desc: 'Voir le parcours d’étude et corriger les points faibles pour 60/80/100+.', cta: 'Voir le parcours' },
  ar: { title: 'خطة الدرجة المستهدفة', desc: 'اطّلع على مسار الدراسة ومعالجة نقاط الضعف لأهداف 60/80/100+.', cta: 'عرض مسار الدراسة' },
  'pt-BR': { title: 'Plano de Pontuação', desc: 'Veja a rota de estudo e como corrigir fraquezas para metas 60/80/100+.', cta: 'Ver rota de estudo' },
  'zh-TW': { title: '目標分數計畫', desc: '立即查看 60/80/100+ 目標學習路線與弱點補強方式。', cta: '查看學習路線' },
  'zh-CN': { title: '目标分数计划', desc: '立即查看 60/80/100+ 目标学习路径和弱点补强方法。', cta: '查看学习路径' },
};

export const trustLabels: Record<Lang, string> = {
  ko: '환불 정책 사전 고지 · 언제든 해지 가능 · 안전 결제',
  ja: '14日返金保証 · いつでも解約可能 · 安全決済',
  vi: 'Hoàn tiền 14 ngày · Hủy bất cứ lúc nào · Thanh toán an toàn',
  th: 'คืนเงินภายใน 14 วัน · ยกเลิกได้ทุกเมื่อ · ชำระเงินปลอดภัย',
  id: 'Kebijakan refund dijelaskan · Bisa batal kapan saja · Pembayaran aman',
  en: 'Refund policy disclosed · Cancel anytime · Secure checkout',
  ms: 'Polisi bayaran balik dinyatakan jelas · Boleh batal bila-bila masa · Pembayaran selamat',
  my: '၁၄ ရက်အတွင်း ငွေပြန်အမ်း · အချိန်မရွေး ရပ်နိုင် · လုံခြုံသော ငွေပေးချေမှု',
  de: '14-Tage-Rückerstattung · Jederzeit kündbar · Sichere Zahlung',
  es: 'Reembolso en 14 días · Cancela cuando quieras · Pago seguro',
  fr: 'Politique de remboursement communiquée · Annulation à tout moment · Paiement sécurisé',
  ar: 'ضمان استرداد خلال 14 يومًا · إلغاء في أي وقت · دفع آمن',
  'pt-BR': 'Garantia de reembolso em 14 dias · Cancele quando quiser · Pagamento seguro',
  'zh-TW': '14 天退款保證 · 隨時可取消 · 安全付款',
  'zh-CN': '14 天退款保障 · 可随时取消 · 安全支付',
};

export const headlineLabels = (n: string): Record<Lang, Headline> => ({
  ko: { l1: 'Power TOEFL — 2026 토플 연습', l2: '모의고사·섹션 연습·가이드. 무료 모의는 전용 페이지에서' },
  ja: { l1: `無料TOEFL模試 ${n}+問題`, l2: 'AIで弱点をすぐ見つけるTOEFL対策' },
  'zh-CN': { l1: `免费托福模考 ${n}+ 题`, l2: '用 AI 快速找出弱点的托福练习' },
  'zh-TW': { l1: `免費托福模考 ${n}+ 題`, l2: '用 AI 快速找出弱點的托福練習' },
  vi: { l1: `Mock test TOEFL miễn phí ${n}+ câu`, l2: 'Luyện TOEFL với AI để biết điểm yếu ngay' },
  th: { l1: `Mock TOEFL ฟรี ${n}+ ข้อ`, l2: 'ฝึก TOEFL ด้วย AI เพื่อเห็นจุดอ่อนทันที' },
  id: { l1: `Mock test TOEFL gratis ${n}+ soal`, l2: 'Latihan TOEFL dengan AI untuk tahu titik lemah Anda' },
  ms: { l1: `Mock test TOEFL percuma ${n}+ soalan`, l2: 'Latihan TOEFL dengan AI untuk lihat kelemahan anda' },
  my: { l1: `အခမဲ့ TOEFL Mock ${n}+ မေးခွန်း`, l2: 'AI ဖြင့် အားနည်းချက်ကို ချက်ချင်းရှာဖွေသော TOEFL လေ့ကျင့်မှု' },
  de: { l1: `Kostenloser TOEFL-Mock ${n}+ Fragen`, l2: 'KI-TOEFL-Training, das Schwächen sofort sichtbar macht' },
  es: { l1: `TOEFL gratuito con ${n}+ preguntas`, l2: 'Práctica TOEFL con IA para detectar debilidades al instante' },
  fr: { l1: `TOEFL gratuit – ${n}+ questions`, l2: 'Pratique TOEFL avec IA pour repérer vos points faibles' },
  'pt-BR': { l1: `Simulado TOEFL grátis ${n}+ questões`, l2: 'Prática TOEFL com IA para identificar suas fraquezas agora' },
  ar: { l1: `تجريبي TOEFL مجاني ${n}+ سؤال`, l2: 'تدريب TOEFL بالذكاء الاصطناعي لتحديد نقاط الضعف فورًا' },
  en: { l1: `Free TOEFL mock test · ${n}+ questions`, l2: 'AI practice that shows your weak spots fast' },

});

export const primaryCtaLabels: Record<Lang, string> = {
  ko: '지금 무료 모의고사 시작',
  ja: '無料模試でスコア確認',
  'zh-CN': '用免费模考确认分数',
  'zh-TW': '用免費模考確認分數',
  vi: 'Kiểm tra điểm bằng mock test miễn phí',
  th: 'เช็กคะแนนด้วย Mock Test ฟรี',
  id: 'Cek skor dengan mock test gratis',
  ms: 'Semak skor dengan mock test percuma',
  my: 'အခမဲ့ mock test ဖြင့် ရမှတ်စစ်ဆေး',
  de: 'Score mit kostenlosem Mock-Test prüfen',
  es: 'Revisa tu puntuación con un simulacro gratis',
  fr: 'Vérifier son score avec un mock test gratuit',
  'pt-BR': 'Confira sua pontuação com simulado grátis',
  ar: 'تحقق من درجتك عبر اختبار مجاني',
  en: 'Check your score with a free mock test',
};
